import React, { useEffect, useRef, useState } from 'react';

// New UI, invoice editor: drag a box on the document to zoom into it; right-click steps back out, one zoom at a time,
// then to the starting size. Rendered inside the viewer's #pdfContainer (InvoiceDocumentViewer, info.dragZoom). PDFs are
// redrawn sharp at the new scale by the viewer (onZoom); images are sized with CSS.
export const MAX_SCALE = 4;
export const MIN_DRAG = 10;
export const HINT = 'Drag to zoom in · right-click to zoom out';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// The scale at which a w x h box (CSS px, drawn at `scale`) fills a viewW x viewH view; never below `scale` or above max.
export function fitScale(scale, w, h, viewW, viewH, max = MAX_SCALE) {
	if (!(w > 0 && h > 0 && viewW > 0 && viewH > 0)) return scale;
	return Math.round(clamp(scale * Math.min(viewW / w, viewH / h), scale, Math.max(scale, max)) * 100) / 100;
}

// Scroll offsets that centre a point (x, y: px from the document's corner at scale `from`) after zooming to `to`.
export function centreOn(x, y, from, to, doc, view) {
	return {
		left: Math.max(0, Math.round(doc.left + x / from * to - view.width / 2)),
		top: Math.max(0, Math.round(doc.top + y / from * to - view.height / 2))
	};
}

// The drag box between two points, kept inside bounds (the part of the document in view).
export function dragBox(a, b, bounds) {
	const x1 = clamp(a.x, bounds.left, bounds.right), x2 = clamp(b.x, bounds.left, bounds.right);
	const y1 = clamp(a.y, bounds.top, bounds.bottom), y2 = clamp(b.y, bounds.top, bounds.bottom);
	return { left: Math.min(x1, x2), top: Math.min(y1, y2), width: Math.abs(x2 - x1), height: Math.abs(y2 - y1) };
}

export default function DragZoom({ scale, onZoom, fitWidth }) {
	const layerRef = useRef(null);
	const [box, setBox] = useState(null);
	const live = useRef({ scale, onZoom, imgScale: 1, history: [], base: 1 });
	live.current.scale = scale;
	live.current.onZoom = onZoom;

	useEffect(() => {
		const container = layerRef.current && layerRef.current.parentElement;
		if (!container) return undefined;
		const my = live.current;
		const shown = () => {
			const canvas = container.querySelector('canvas[id^="the-canvas-"]');
			if (canvas && canvas.offsetWidth) return { el: canvas, pdf: true };
			const img = container.querySelector('#imgInvoiceDocument');
			if (img && img.offsetWidth) return { el: img, pdf: false };
			return null;
		};
		const scaleOf = doc => (doc.pdf ? my.scale : my.imgScale);
		const apply = (doc, s, done) => {
			if (doc.pdf) { my.onZoom(s, done); return; }
			my.imgScale = s;
			Object.assign(doc.el.style, s > 1 ? { width: s * 100 + '%', maxWidth: 'none', flexShrink: '0' } : { width: '', maxWidth: '', flexShrink: '' });
			container.style.justifyContent = s > 1 ? 'flex-start' : '';
			requestAnimationFrame(done);
		};
		const inView = doc => {
			const d = doc.el.getBoundingClientRect(), c = container.getBoundingClientRect();
			const left = c.left + container.clientLeft, top = c.top + container.clientTop;
			return {
				left: Math.max(d.left, left), top: Math.max(d.top, top),
				right: Math.min(d.right, left + container.clientWidth), bottom: Math.min(d.bottom, top + container.clientHeight)
			};
		};
		const inLayer = r => {
			const c = container.getBoundingClientRect();
			return {
				left: r.left - c.left - container.clientLeft + container.scrollLeft, top: r.top - c.top - container.clientTop + container.scrollTop,
				width: r.width, height: r.height
			};
		};

		let drag = null;
		const stop = () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			window.removeEventListener('keydown', onKey);
			setBox(null);
		};
		const onDown = e => {
			if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
			const doc = shown();
			if (!doc || e.target !== doc.el) return;
			e.preventDefault(); // no text selection or image drag; move focus off the form the way a click would
			const active = document.activeElement;
			if (active && active !== document.body && active.blur) active.blur();
			drag = { doc, start: { x: e.clientX, y: e.clientY }, bounds: inView(doc) };
			window.addEventListener('mousemove', onMove);
			window.addEventListener('mouseup', onUp);
			window.addEventListener('keydown', onKey);
		};
		const onMove = e => {
			const r = dragBox(drag.start, { x: e.clientX, y: e.clientY }, drag.bounds);
			if (!drag.moved && r.width < MIN_DRAG && r.height < MIN_DRAG) return;
			drag.moved = true;
			setBox(inLayer(r));
		};
		const onKey = e => { if (e.key === 'Escape') { drag = null; stop(); } };
		const onUp = e => {
			stop();
			const { doc, start, bounds } = drag;
			drag = null;
			const r = dragBox(start, { x: e.clientX, y: e.clientY }, bounds);
			if (r.width < MIN_DRAG || r.height < MIN_DRAG) return;
			const cs = getComputedStyle(container);
			const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight), padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
			const maxH = parseFloat(cs.maxHeight);
			const viewW = container.clientWidth - padX, viewH = Math.max(container.clientHeight, maxH || 0) - padY;
			const from = scaleOf(doc), to = fitScale(from, r.width, r.height, viewW, viewH);
			const d = doc.el.getBoundingClientRect();
			const x = r.left + r.width / 2 - d.left, y = r.top + r.height / 2 - d.top;
			my.history.push({ scale: from, left: container.scrollLeft, top: container.scrollTop });
			apply(doc, to, () => {
				const at = centreOn(x, y, from, to, { left: doc.el.offsetLeft, top: doc.el.offsetTop }, { width: container.clientWidth, height: container.clientHeight });
				container.scrollLeft = at.left;
				container.scrollTop = at.top;
			});
		};
		const onMenu = e => {
			const doc = shown();
			if (!doc) return;
			const base = doc.pdf ? my.base : 1;
			const back = my.history.pop() || (scaleOf(doc) > base ? { scale: base, left: 0, top: 0 } : null);
			if (!back) return; // at the starting size: the browser's own menu
			e.preventDefault();
			apply(doc, back.scale, () => { container.scrollLeft = back.left; container.scrollTop = back.top; });
		};
		container.addEventListener('mousedown', onDown);
		container.addEventListener('contextmenu', onMenu);
		container.querySelectorAll('canvas[id^="the-canvas-"], #imgInvoiceDocument').forEach(el => { el.title = HINT; });
		return () => {
			container.removeEventListener('mousedown', onDown);
			container.removeEventListener('contextmenu', onMenu);
			stop();
		};
	}, []);

	// fitWidth (Vendor Mapping): open a PDF at the width of the viewer instead of 100%, and make that the size
	// right-click returns to. Only until somebody zooms. The viewer
	// stores the page size right after its first render with a copy of its state (which still says 100%), so the fit
	// waits for that render to settle and tries again if it was undone.
	useEffect(() => {
		if (!fitWidth) return undefined;
		const container = layerRef.current && layerRef.current.parentElement;
		if (!container) return undefined;
		const my = live.current;
		let raf = 0, timer = 0, tries = 0, attempts = 0;
		const target = canvas => {
			const cs = getComputedStyle(container);
			const avail = container.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - 4;
			return Math.floor(Math.min(MAX_SCALE, avail / (canvas.offsetWidth / my.scale)) * 100) / 100;
		};
		const apply = () => {
			const canvas = container.querySelector('canvas[id^="the-canvas-"]');
			if (!canvas || !canvas.offsetWidth || my.history.length || attempts > 3) return;
			if (my.scale !== 1 && my.scale !== my.base) return; // the user zoomed
			const s = target(canvas);
			if (s < 0.6 || Math.abs(s - 1) <= 0.02 || Math.abs(my.scale - s) < 0.02) return; // also shrinks a page a little wider than the viewer
			attempts++;
			my.base = s;
			my.onZoom(s, () => {});
			timer = setTimeout(apply, 700); // check it held
		};
		const wait = () => {
			const canvas = container.querySelector('canvas[id^="the-canvas-"]');
			if (!canvas || !canvas.offsetWidth) { if (++tries < 600) raf = requestAnimationFrame(wait); return; }
			timer = setTimeout(apply, 300);
		};
		raf = requestAnimationFrame(wait);
		return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
	}, [fitWidth]);

	return (
		<div ref={layerRef} className={'apw2-dz-layer' + (box ? ' on' : '')} aria-hidden="true">
			{box ? <span className="apw2-dz-box" style={box} /> : null}
		</div>
	);
}
